/**
 * TrlGameSettingsModal — this Trailblazers game's settings, in six tabs.
 *
 *   Triangulation   — how a 3-screenshot planet is completed.
 *   Stalemate Clues — the clue ladder that nudges a stalled team.
 *   Radar Ranges    — the bands every dig is scored against.
 *   Disruption      — whether enemy Sparkle Powder can block a scanner.
 *   Sabotage        — which pre-game planet sabotages teams may place.
 *   Treasure List   — the reward pool and how it is spread over the map.
 *
 * Opened from the map-builder toolbar. All tabs but Treasure persist through their
 * own socket events; the Treasure List splits in two — the POOL is game-level
 * config (its own socket event), while the per-planet ASSIGNMENTS live on the
 * map nodes and are saved with the map. Rather than make the admin press two
 * buttons, "Save settings" persists the pool and then triggers the builder's own
 * save when assignments have changed.
 *
 * Saving does NOT close the modal — the admin is usually part-way through a longer
 * pass over several tabs. Save is disabled until something actually changes and goes
 * back to disabled once it lands; closing is always the admin's own decision, and is
 * confirmed while there is unsaved work.
 *
 * (Formerly StalemateDefaultsModal, which had long since outgrown its name.)
 */

import { useMemo, useRef, useState } from 'react';
import { type Tab } from '@swtorbit/shared-components';
import { Modal, Tabs } from '@swtorbit/ui/react';
import { ConfirmModal } from '../../../ui/ConfirmModal';
import {
  DEFAULT_TRL_STALEMATE_CONFIG,
  DEFAULT_TRL_RADAR_CONFIG,
  DEFAULT_TRL_TREASURE_CONFIG,
  DEFAULT_TRL_TRIANGULATION_CONFIG,
  DEFAULT_TRL_DISRUPTION_CONFIG,
  DEFAULT_TRL_SABOTAGE_CONFIG,
  TRL_SIMULTANEOUS_MIN_TEAM_SIZE,
  resolveTrlConsolationConfig,
  type TrlStalemateConfig,
  type TrlRadarConfig,
  type TrlTreasureConfig,
  type TrlTriangulationConfig,
  type TrlDisruptionConfig,
  type TrlSabotageConfig,
  type TrlTeam,
  type TrlMapNodeDef,
} from '@swtorbit/shared-socket-events';
import { useTrlContext } from '../../../contexts/TrlContext';
import { emitConfigWrite } from './configWrite';
import { StalemateConfigFields } from './StalemateConfigFields';
import { TriangulationConfigFields } from './TriangulationConfigFields';
import { RadarConfigFields } from './RadarConfigFields';
import { DisruptionConfigFields } from './DisruptionConfigFields';
import { SabotageConfigFields } from './SabotageConfigFields';
import { TreasureListFields } from './treasure/TreasureListFields';
import { TreasureAssignmentPanel } from './treasure/TreasureAssignmentPanel';
import { ConsolationPoolFields } from './treasure/ConsolationPoolFields';

// Order matters twice over: it is the tab order, and `TABS[0]` is the tab the
// modal opens on.
const TABS: Tab[] = [
  { id: 'triangulation', label: 'Triangulation' },
  { id: 'stalemate', label: 'Stalemate Clues' },
  { id: 'radar', label: 'Radar Ranges' },
  { id: 'disruption', label: 'Disruption' },
  { id: 'sabotage', label: 'Sabotage' },
  { id: 'treasure', label: 'Treasure List' },
];

/**
 * A comparable snapshot of the six config blocks, for dirty tracking. Order is
 * fixed, so two snapshots of equal configs always match.
 */
function serializeConfigs(
  triangulation: TrlTriangulationConfig,
  stalemate: TrlStalemateConfig,
  radar: TrlRadarConfig,
  disruption: TrlDisruptionConfig,
  sabotage: TrlSabotageConfig,
  treasure: TrlTreasureConfig,
): string {
  return JSON.stringify([triangulation, stalemate, radar, disruption, sabotage, treasure]);
}

interface TrlGameSettingsModalProps {
  roomCode: string;
  /** Current game-level stalemate config (undefined → built-in defaults). */
  current?: TrlStalemateConfig | null;
  /** Current game-level radar config (undefined → built-in defaults). */
  currentRadar?: TrlRadarConfig | null;
  /** Current game-level treasure pool (undefined → empty pool). */
  currentTreasure?: TrlTreasureConfig | null;
  /** Current game-level triangulation rules (undefined → built-in defaults). */
  currentTriangulation?: TrlTriangulationConfig | null;
  /** Current game-level disruption rules (undefined → disabled). */
  currentDisruption?: TrlDisruptionConfig | null;
  /** Current game-level sabotage rules (undefined → every type disabled). */
  currentSabotage?: TrlSabotageConfig | null;
  /** This game's teams, for the simultaneous-broadcast team-size warning. */
  teams?: TrlTeam[];
  /** Live map nodes, for the treasure distribution view. */
  nodes: TrlMapNodeDef[];
  /** Apply a new nodeId → itemIds mapping to the builder's node state. */
  onTreasureAssignmentsChange: (assignments: Record<string, string[]>) => void;
  /** Persist the map (called after a successful save when assignments changed). */
  onSaveMap: () => void;
  onClose: () => void;
}

export function TrlGameSettingsModal({
  roomCode,
  current,
  currentRadar,
  currentTreasure,
  currentTriangulation,
  currentDisruption,
  currentSabotage,
  teams,
  nodes,
  onTreasureAssignmentsChange,
  onSaveMap,
  onClose,
}: TrlGameSettingsModalProps) {
  const {
    updateStalemateConfig,
    updateRadarConfig,
    updateTreasureConfig,
    updateTriangulationConfig,
    updateDisruptionConfig,
    updateSabotageConfig,
  } = useTrlContext();

  const [activeTab, setActiveTab] = useState<string>(TABS[0].id);
  const [config, setConfig] = useState<TrlStalemateConfig>(current ?? DEFAULT_TRL_STALEMATE_CONFIG);
  const [radar, setRadar] = useState<TrlRadarConfig>(currentRadar ?? DEFAULT_TRL_RADAR_CONFIG);
  const [treasure, setTreasure] = useState<TrlTreasureConfig>(currentTreasure ?? DEFAULT_TRL_TREASURE_CONFIG);
  const [triangulation, setTriangulation] = useState<TrlTriangulationConfig>(
    currentTriangulation ?? DEFAULT_TRL_TRIANGULATION_CONFIG,
  );
  const [disruption, setDisruption] = useState<TrlDisruptionConfig>(
    currentDisruption ?? DEFAULT_TRL_DISRUPTION_CONFIG,
  );
  const [sabotage, setSabotage] = useState<TrlSabotageConfig>(currentSabotage ?? DEFAULT_TRL_SABOTAGE_CONFIG);

  // Warn before saving rather than only on the rejected start: the server refuses
  // to enable simultaneous broadcast while any team is short.
  const undersizedTeamNames = (teams ?? [])
    .filter((t) => t.members.length < TRL_SIMULTANEOUS_MIN_TEAM_SIZE)
    .map((t) => `${t.name} (${t.members.length})`);
  const [assignmentsDirty, setAssignmentsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  /**
   * The last-saved snapshot of the six configs, as the baseline `isDirty` compares
   * against. Held here rather than read back off the `current*` props: those are
   * seeded into state exactly once (above) and a server broadcast could move them
   * out from under us mid-edit, which would silently mis-report dirtiness.
   *
   * These are plain JSON-serialisable config objects, so a stringify compare is
   * enough — and it keeps every field's `onChange` a bare setter, with no `touch()`
   * to remember to thread through six tab components.
   */
  const savedBaselineRef = useRef<string>(
    serializeConfigs(triangulation, config, radar, disruption, sabotage, treasure),
  );
  // Bumped on a successful save so the memo below re-reads the ref.
  const [savedTick, setSavedTick] = useState(0);

  const isDirty = useMemo(
    () =>
      serializeConfigs(triangulation, config, radar, disruption, sabotage, treasure) !==
        savedBaselineRef.current || assignmentsDirty,
    [triangulation, config, radar, disruption, sabotage, treasure, assignmentsDirty, savedTick],
  );

  const handleAssignmentsChange = (assignments: Record<string, string[]>) => {
    onTreasureAssignmentsChange(assignments);
    setAssignmentsDirty(true);
  };

  /** Close, but confirm first when there is unsaved work to lose. */
  const requestClose = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  /**
   * Fire all six config writes and report which (if any) failed.
   *
   * They are independent server-side, so we run them together rather than chaining
   * and leaving the outcome ambiguous. Saving never closes the modal — the admin
   * keeps working and closes when they choose — so a clean save just re-baselines
   * and drops the Save button back to disabled.
   *
   * A partial failure leaves the successful writes committed but does NOT
   * re-baseline, so Save stays enabled for a retry. Pressing it again is safe,
   * since each write is a full replace.
   */
  const handleSave = () => {
    setSaving(true);
    setError(null);

    void Promise.all([
      emitConfigWrite((cb) => updateTriangulationConfig({ roomCode, config: triangulation }, cb), 'triangulation'),
      emitConfigWrite((cb) => updateStalemateConfig({ roomCode, config }, cb), 'stalemate clues'),
      emitConfigWrite((cb) => updateRadarConfig({ roomCode, config: radar }, cb), 'radar ranges'),
      emitConfigWrite((cb) => updateDisruptionConfig({ roomCode, config: disruption }, cb), 'disruption'),
      emitConfigWrite((cb) => updateSabotageConfig({ roomCode, config: sabotage }, cb), 'sabotage'),
      emitConfigWrite((cb) => updateTreasureConfig({ roomCode, config: treasure }, cb), 'treasure list'),
    ]).then((results) => {
      setSaving(false);
      const failures = results.filter((r): r is string => r !== null);
      if (failures.length > 0) {
        setError(failures.join(' · '));
        return;
      }
      // Assignments ride on the map, not on the settings write. `onSaveMap` is the
      // builder's own fire-and-forget save, so clearing the flag here is optimistic —
      // acceptable, because the builder keeps its own independent dirty flag and its
      // toolbar SAVE stays lit if the map write fails.
      if (assignmentsDirty) onSaveMap();
      setAssignmentsDirty(false);

      savedBaselineRef.current = serializeConfigs(
        triangulation,
        config,
        radar,
        disruption,
        sabotage,
        treasure,
      );
      setSavedTick((n) => n + 1);
      setLastSavedAt(Date.now());
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'triangulation':
        return (
          <TriangulationConfigFields
            value={triangulation}
            onChange={setTriangulation}
            undersizedTeamNames={undersizedTeamNames}
          />
        );
      case 'stalemate':
        return <StalemateConfigFields value={config} onChange={setConfig} />;
      case 'radar':
        return <RadarConfigFields value={radar} onChange={setRadar} />;
      case 'disruption':
        return <DisruptionConfigFields value={disruption} onChange={setDisruption} />;
      case 'sabotage':
        return <SabotageConfigFields value={sabotage} onChange={setSabotage} />;
      case 'treasure':
        return (
          <div className="trl-treasure-layout">
            <div className="trl-treasure-layout__panel">
              <TreasureListFields value={treasure} onChange={setTreasure} nodes={nodes} />
              {/* Boxed like the tier curves above it: a distinct sub-block of the same tab,
                  not a second list competing with the pool for attention. */}
              <div className="border-md border-blue-light rounded-sm p-xs mt-xs">
                <ConsolationPoolFields
                  value={resolveTrlConsolationConfig(treasure)}
                  onChange={(consolation) => setTreasure({ ...treasure, consolation })}
                />
              </div>
            </div>
            <div className="trl-treasure-layout__panel">
              <TreasureAssignmentPanel
                nodes={nodes}
                config={treasure}
                onAssignmentsChange={handleAssignmentsChange}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* A native dialog: Escape closes only the topmost one, so the discard
          confirm above it never closes this too. */}
      <Modal
        open
        onClose={requestClose}
        className="trl-game-settings-modal"
        title="Game Settings"
        footer={
          <>
            {error && <span className="text-red fs-sm">{error}</span>}
            <div className="d-flex gap-xs ms-auto">
              <button type="button" className="btn btn--outline btn--slate" onClick={requestClose} disabled={saving}>
                Close
              </button>
              <button
                type="button"
                className="btn btn--green"
                onClick={handleSave}
                disabled={saving || !isDirty}
                title={lastSavedAt ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}` : undefined}
              >
                {saving ? 'Saving…' : isDirty ? 'Save settings' : 'Saved'}
              </button>
            </div>
          </>
        }
      >
        <Tabs variant="bordered" items={TABS.map((t) => ({ value: t.id, label: t.label }))} value={activeTab} onChange={setActiveTab}>{renderTabContent()}</Tabs>
      </Modal>

      <ConfirmModal
        isOpen={discardConfirmOpen}
        title="Discard unsaved changes?"
        message="This modal has settings changes that have not been saved. Closing now loses them."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="danger"
        onConfirm={onClose}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
}
